import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { listInsights, mapApiInsightToUi } from '../../../services/insights.js';
import { apiErrorStatus } from './apiErrorStatus.js';

const EMPTY_EMERGING = Object.freeze({ count: 0, items: [] });
const EMPTY_INSIGHTS = Object.freeze({
  recordKey: null,
  items: [],
  emerging: EMPTY_EMERGING,
  interactionsSoFar: null,
  paused: null,
  total: 0,
});

/** One response, as one value: everything in it changes together. */
const mapInsightsResponse = (recordKey, { body, meta }) => {
  const items = (body?.items ?? []).map(mapApiInsightToUi);
  return {
    recordKey,
    items,
    emerging: {
      count: body?.emerging?.count ?? 0,
      items: (body?.emerging?.items ?? []).map(mapApiInsightToUi),
    },
    interactionsSoFar: body?.interactions_so_far ?? body?.interactionsSoFar ?? null,
    paused: body?.paused ? { since: body.paused.since ?? null, source: body.paused.source ?? null } : null,
    total: meta?.total_items ?? meta?.totalItems ?? items.length,
  };
};

/**
 * The Insights of one record for the selected chip, status filter, search and
 * sort (CT-5716). Same shape as `useTraits`: a race guard, serialized keys, a
 * memoized return; the server narrows.
 *
 * Besides the cards it surfaces what picks the page's state: the emerging group,
 * how many interactions there are when no pattern holds yet, and whether
 * insights are paused (and since when).
 *
 * A failed request keeps the last answer for THIS record, as the prototype does
 * ("Couldn't load insights · Retry" above the cards it already had); another
 * record's answer is never shown. From the first paint after the record changes
 * until its own answer (or failure) arrives, the hook reads as loading — never
 * as an empty list, which would flash "No insights" before the skeleton.
 */
export function useInsights({ entityType, entityId, quickFilter = 'all', orderBy, orderDirection, filters, search } = {}) {
  const [data, setData] = useState(EMPTY_INSIGHTS);
  const [loading, setLoading] = useState(true);
  /** `{ recordKey, message, status }` of the last failed request, or null. */
  const [failure, setFailure] = useState(null);
  const fetchIdRef = useRef(0);

  const recordKey = `${entityType}/${entityId}`;
  const filtersKey = JSON.stringify(filters ?? {});
  const searchKey = JSON.stringify(search ?? null);

  const fetchData = useCallback(async () => {
    if (!entityType || !entityId) return;
    const requestId = ++fetchIdRef.current;
    const key = `${entityType}/${entityId}`;
    setLoading(true);
    setFailure(null);

    try {
      const parsedSearch = JSON.parse(searchKey);
      const response = await listInsights({
        entityType,
        entityId,
        quickFilter,
        orderBy,
        orderDirection,
        filters: JSON.parse(filtersKey),
        ...(parsedSearch ? { search: parsedSearch } : {}),
      });
      if (requestId !== fetchIdRef.current) return;
      setData(mapInsightsResponse(key, response));
    } catch (err) {
      if (requestId !== fetchIdRef.current) return;
      setFailure({ recordKey: key, message: err?.message ?? 'Failed to load the insights', status: apiErrorStatus(err) });
    } finally {
      if (requestId === fetchIdRef.current) setLoading(false);
    }
  }, [entityType, entityId, quickFilter, orderBy, orderDirection, filtersKey, searchKey]);

  useEffect(() => {
    fetchData();
    return () => {
      fetchIdRef.current += 1;
    };
  }, [fetchData]);

  // What is kept from an earlier answer or failure belongs to this record, or is nothing.
  const current = data.recordKey === recordKey ? data : EMPTY_INSIGHTS;
  const currentFailure = failure?.recordKey === recordKey ? failure : null;
  // Neither an answer nor a failure for this record yet: it is loading, whatever the flag says.
  const waiting = loading || (data.recordKey !== recordKey && !currentFailure);
  const error = currentFailure?.message ?? null;
  const status = currentFailure?.status ?? null;

  return useMemo(
    () => ({
      items: current.items,
      emerging: current.emerging,
      interactionsSoFar: current.interactionsSoFar,
      paused: current.paused,
      total: current.total,
      loading: waiting,
      error,
      accessDenied: status === 403,
      refetch: fetchData,
    }),
    [current, waiting, error, status, fetchData],
  );
}

export default useInsights;
